import { TrophyOutlined } from "@ant-design/icons";
import { Icons, Tooltip, Typography, formatNumber } from "@ster/ster-toolkit";
import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { useRecoilState } from "recoil";

import {
  SterWebsiteModelPriceCompareCalculateResultItem,
  SterWebsiteModelPriceCompareMetadata,
} from "./api";
import { targetGroupVisibleSelector } from "./atoms";
import { Campaign } from "./Compare";
import HelpText from "./HelpText";
import styles from "./ResultItem.module.less";

const ResultItem = ({
  result,
  campaigns,
  onDelete,
  onlyLabels,
  metadata,
}: {
  result: SterWebsiteModelPriceCompareCalculateResultItem;
  campaigns: Partial<Campaign>[];
  onDelete?: (value: string) => void;
  onlyLabels?: boolean;
  metadata?: SterWebsiteModelPriceCompareMetadata;
}): JSX.Element => {
  const showAwards = useMemo(() => campaigns.length > 1, [campaigns.length]);
  const [isVisible, setIsVisible] = useRecoilState(
    targetGroupVisibleSelector(result.targetGroupName)
  );

  const changeVisible = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible, setIsVisible]);

  return (
    <>
      {onlyLabels && (
        <Typography.Title
          level={3}
          className={classNames(styles.title, {
            [styles.collapsed]: !isVisible,
          })}
        >
          {onDelete !== undefined && (
            <Tooltip title="Verwijder deze doelgroep">
              <button
                className={styles.delete}
                type="button"
                onClick={(): void => onDelete?.(result.targetGroupName)}
              >
                <Icons.DeleteIcon />
              </button>
            </Tooltip>
          )}
          <span
            className={styles.toggle}
            onClick={changeVisible}
            role="button"
            tabIndex={0}
          >
            {isVisible ? <Icons.ChevronDownIcon /> : <Icons.ChevronRightIcon />}
          </span>
          {metadata?.targetGroups.find(
            (s) => s.value === result.targetGroupName
          )?.label ?? result.targetGroupName}
        </Typography.Title>
      )}
      <div
        className={classNames(styles.results, {
          [styles.onlyLabels]: onlyLabels,
        })}
      >
        {onlyLabels && (
          <div
            className={classNames(styles.labels, {
              [styles.visible]: isVisible,
            })}
          >
            <span>1+ bereik %</span>
            <span>1+ bereik #</span>
            <span>
              GCF{" "}
              <HelpText
                text={
                  <>
                    Gemiddelde Contact Frequentie, oftewel: hoe vaak ziet of
                    hoort de doelgroep de commercial voorbij komen.
                  </>
                }
              />
            </span>
            <span>GRP&apos;s #</span>
            <span>€/GRP</span>
          </div>
        )}
        {!onlyLabels &&
          campaigns.map((s) => {
            const item = result.results.find((r) => r.campaignId === s.id);
            return item ? (
              <div className={styles.container} key={item.campaignId}>
                <div
                  className={classNames(styles.operator, {
                    [styles.visible]: isVisible,
                  })}
                >
                  <Typography.Title level={3}>
                    {
                      metadata?.operators.find((o) => o.value === s.operator)
                        ?.label
                    }
                  </Typography.Title>
                </div>
                <div
                  className={classNames(styles.resultItem, {
                    [styles.visible]: isVisible,
                  })}
                  key={JSON.stringify(s)}
                >
                  <div className={styles.resultRow}>
                    {showAwards &&
                      Math.max(
                        ...result.results.map((r) => r.onePlusReachPercentage)
                      ) === item.onePlusReachPercentage && (
                        <div className={styles.award}>
                          hoogste bereik <TrophyOutlined />
                        </div>
                      )}
                    <span>{formatNumber(item.onePlusReachPercentage, 1)}%</span>
                  </div>
                  <span>{formatNumber(item.onePlusReachAbsolute)}</span>

                  <div className={styles.resultRow}>
                    {showAwards &&
                      Math.max(...result.results.map((r) => r.gcf)) ===
                        item.gcf && (
                        <div className={styles.award}>
                          hoogste GCF <TrophyOutlined />
                        </div>
                      )}
                    <span>{formatNumber(item.gcf, 1)}</span>
                  </div>
                  <span>{formatNumber(item.grps, 1)}</span>
                  <div className={styles.resultRow}>
                    {showAwards &&
                      Math.min(...result.results.map((r) => r.pricePerGrp)) ===
                        item.pricePerGrp && (
                        <div className={styles.award}>
                          beste prijs <TrophyOutlined />
                        </div>
                      )}
                    {item.pricePerGrp !== undefined ? (
                      <span>€ {formatNumber(item.pricePerGrp ?? 0, 0)}</span>
                    ) : (
                      <span>n.v.t.</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={classNames(styles.resultItem, styles.empty)}
                key={JSON.stringify(s)}
              />
            );
          })}
      </div>
    </>
  );
};

export default ResultItem;
